/**
 * dp-format-picker.js — Formatkacheln der Produktseite (<dp-format-picker>).
 *
 * Lädt assets/dp-format-map.json per fetch (die Asset-URL kommt als data-map-url
 * aus snippets/dp-format-picker.liquid, weil Liquid keine Asset-Dateien lesen
 * kann), filtert auf die Formate des jeweiligen DP-Produkts, die ab 1 Stück
 * bestellbar sind, und rendert daraus eine Radiogruppe aus Kacheln
 * (echte <input type="radio">, visuell versteckt; die Kachel ist das <label>).
 *
 * Nach außen spricht die Komponente nur über zwei Kanäle:
 *   - Custom Event `dp:format-change` (bubbles, composed) mit
 *     detail = { key, dpProductId, variantId, attrs, priceFrom, tiers, label, … } —
 *     bei jeder Auswahl, einmal nach dem ersten Rendern und bei jeder Änderung der
 *     Papier-/Bindungs-Radios der Seite (input[data-dp-option], snippets/dp-option-swatch.liquid).
 *     Preis und Designer-Link in der Kaufspalte hören darauf (sections/dp-product-main.liquid,
 *     {% javascript %}-Block), ebenso der Preisrechner (dp-staffel-rechner.js).
 *     Seit 2026-09-21 (QA F-01) gelten priceFrom/tiers/secondCopyExtra für die GEWÄHLTE
 *     Papier/Bindungs-Kombination (products[dpId].options in der Map, dp-designer-link.js
 *     resolveOptionOffer); dazu detail.selectedOptions = { paper, binding }, detail.optionsResolved
 *     (false: kein Datensatz → Standardpreis, Aufpreis erst im Bestellschritt), detail.standardPriceFrom
 *     und detail.optionExtra (Aufpreis gegenüber dem Standard).
 *   - sessionStorage['dp:selectedFormat'] = dasselbe detail als JSON.
 *
 * Familienregel (bewusst je DP-Produkt festgelegt, nicht aus den Daten geraten):
 * Produkt 2446 (Fotokalender) führt in derselben Katalog-Gruppe auch
 * tischkalender_*-Einträge — das ist ein anderes Modell und würde auf der
 * Fotokalender-Seite verwirren, deshalb dort nur wandkalender_*. Produkt 10
 * (Tischkalender) zeigt tischkalender_*, Produkt 102 (Wandkalender) wandkalender_*.
 *
 * Reine Hilfsfunktionen (Filtern, Sortieren, Benennen) sind exportiert und
 * DOM-frei, damit sie sich mit `node` gegen die echte JSON prüfen lassen.
 */

import { Component } from '@theme/component';
// ?fmt= der Produktseiten-URL (Startseiten-Kacheln, Megamenü, Fußzeile verlinken /products/<handle>?fmt=<key>).
import { readDesignerParams, resolveOptionOffer, normalizeOptionKeys, expressOfferFrom } from './dp-designer-link.js?v=79b02de54d';

export const FORMAT_CHANGE_EVENT = 'dp:format-change';
export const STORAGE_KEY = 'dp:selectedFormat';

const PREVIEW_BOX_PX = 64;
const PREVIEW_MIN_PX = 14;
/** Exponent der Größenstaffel: 0.6 hält A5 (≈31px) und 50×70 (64px) beide lesbar. */
const PREVIEW_SCALE_EXPONENT = 0.6;
const DEFAULT_VISIBLE_COUNT = 6;

/** @type {Record<string, 'wandkalender' | 'tischkalender'>} */
const FAMILY_BY_PRODUCT = { 2446: 'wandkalender', 102: 'wandkalender', 10: 'tischkalender' };

/** @type {Record<string, string>} */
// Wandkalender Fjell (102) hat seit der Produkttrennung (2026-09-16) nur große Formate → A2 hoch.
const DEFAULT_KEY_BY_PRODUCT = { 102: 'wandkalender_a2_hoch' };
const DEFAULT_KEY_BY_FAMILY = {
  wandkalender: 'wandkalender_a4_hoch',
  tischkalender: 'tischkalender_a5_quer',
};

const TEXT = {
  loading: 'Formate und Preise werden geladen …',
  failed:
    'Die Formate konnten gerade nicht geladen werden. Bitte lade die Seite neu – oder wähle das Format weiter unten im Konfigurator.',
  popular: 'Beliebt',
  from: 'ab',
  /** Große Formate: Zielauflösung des Designers unter 300 dpi (dp-format-map.json designerDpi). */
  dpi: (dpi) => `bis ${dpi} dpi`,
  showAll: (count) => `Alle ${count} Formate anzeigen`,
  showLess: 'Weniger Formate anzeigen',
};

const CHECK_ICON =
  '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const centimetres = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

/**
 * @typedef {object} FormatEntry
 * @property {string} key
 * @property {'wandkalender' | 'tischkalender'} family
 * @property {string} label
 * @property {number} widthMm
 * @property {number} heightMm
 * @property {'hoch' | 'quer' | 'quadrat'} orientation
 * @property {Record<string, FormatOffer>} products
 * @property {number} [designerDpi] - Zielauflösung im Designer (300 bis A3; große Wandformate nach der
 *   Canvas-Kante, z. B. DIN A2 hoch 174) – unter 300 zeigt die Kachel „bis 174 dpi“ und die Kaufspalte
 *   den Hinweis für Wandabstand (sections/dp-product-main.liquid, data-dp-format-note)
 *
 * @typedef {object} FormatOffer
 * @property {number} variantId
 * @property {string} [flyerVariantId]
 * @property {number | null} priceFrom
 * @property {{ quantity: number, price: number }[]} [tiers] - Mengenstaffel 1/2/3/5/10 (Verkaufspreis, Standardversand)
 * @property {number | null} [secondCopyExtra] - Aufpreis des zweiten Exemplars (Preis 2 − Preis 1)
 * @property {number} minQuantity
 * @property {boolean} orderableFromOne
 * @property {Record<string, string>} [attrs]
 * @property {Record<string, { priceFrom: number, tiers: { quantity: number, price: number }[] }>} [options] - Preise je
 *   Papier/Bindungs-Kombination („glanz-250|silber“), scripts/build-format-prices.mjs --options
 *
 * @typedef {{ format: FormatEntry, offer: FormatOffer }} PickerEntry
 */

/**
 * Gewählte Papier/Bindung der Produktseite (Radios `input[data-dp-option="paper|binding"]:checked`,
 * snippets/dp-option-swatch.liquid). Fehlen die Radios (Kacheln aus, Tests), gilt der Standard.
 * @param {ParentNode|null|undefined} root
 * @returns {{ paper: string, binding: string }}
 */
export function selectedPageOptions(root) {
  const read = (group) => {
    try {
      const input = root?.querySelector?.(`input[data-dp-option="${group}"]:checked`);
      return input instanceof HTMLInputElement ? input.value : null;
    } catch {
      return null;
    }
  };
  return normalizeOptionKeys({ paper: read('paper'), binding: read('binding') });
}

/** @param {number} value */
export function formatEuro(value) {
  return euro.format(value);
}

/** @param {FormatEntry} format */
export function formatSize(format) {
  return `${centimetres.format(format.widthMm / 10)} × ${centimetres.format(format.heightMm / 10)} cm`;
}

/** @param {FormatEntry} format */
function shortKey(format) {
  return format.key.replace(/^(wandkalender|tischkalender)_/, '');
}

/**
 * Laienfreundlicher Name aus dem Format-Schlüssel. Die Maße stehen bewusst
 * nicht im Namen — sie kommen in jeder Kachel als eigene Zeile (formatSize).
 * @param {FormatEntry} format
 */
export function friendlyName(format) {
  const key = shortKey(format);
  const din = key.match(/^a(\d)_(hoch|quer|lang)$/);
  if (din) return `DIN A${din[1]} ${din[2]}`;
  if (key === 'din_lang') return 'DIN lang';
  if (key.startsWith('quadrat')) return 'Quadrat';
  if (key.startsWith('familienplaner')) return 'Familienplaner';
  if (key.startsWith('panorama')) return 'Panorama';
  if (key.startsWith('streifen')) return 'Streifenkalender';
  if (key.startsWith('mini')) return 'Minikalender';
  if (/^\d+x\d+$/.test(key)) return format.orientation === 'quer' ? 'Großformat quer' : 'Großformat hoch';
  return format.label;
}

/**
 * Sortiergruppen: erst DIN-Formate, dann Quadrate, dann Sonderformate —
 * innerhalb der Gruppe nach Fläche aufsteigend, Hochformat vor Querformat.
 * @param {FormatEntry} format
 */
/**
 * Beliebtheitsreihenfolge je Familie (Kurzschlüssel ohne Familienpräfix). Formate, die hier
 * nicht stehen, folgen danach in der bisherigen Gruppen-/Flächenordnung. Grund: Die ersten
 * sechs Kacheln sind ohne Aufklappen sichtbar – dort müssen die gängigen Formate stehen
 * (A4 hoch zuerst beim Wandkalender, A5 quer zuerst beim Tischkalender), nicht die kleinsten.
 * @type {Record<string, string[]>}
 */
const POPULARITY_BY_FAMILY = {
  wandkalender: ['a4_hoch', 'a4_quer', 'a3_hoch', 'a3_quer', 'a5_hoch', 'a5_quer', 'quadrat_210', 'streifen'],
  tischkalender: ['a5_quer', 'a5_hoch', 'din_lang', 'quadrat_148', 'a6_quer', 'a6_hoch', 'a5_lang'],
};

/** @param {FormatEntry} format */
function popularityRank(format) {
  const list = POPULARITY_BY_FAMILY[format.family] ?? [];
  const index = list.indexOf(shortKey(format));
  return index === -1 ? list.length : index;
}

function groupRank(format) {
  const key = shortKey(format);
  if (/^a\d_/.test(key) || key === 'din_lang') return 0;
  if (key.startsWith('quadrat')) return 1;
  return 2;
}

/**
 * @param {PickerEntry} a
 * @param {PickerEntry} b
 */
function compareEntries(a, b) {
  const popularity = popularityRank(a.format) - popularityRank(b.format);
  if (popularity !== 0) return popularity;
  const rank = groupRank(a.format) - groupRank(b.format);
  if (rank !== 0) return rank;
  const area = a.format.widthMm * a.format.heightMm - b.format.widthMm * b.format.heightMm;
  if (area !== 0) return area;
  return a.format.orientation === 'hoch' ? -1 : 1;
}

/**
 * Familie für unbekannte DP-Produkte: die mit den meisten ab-1-bestellbaren Einträgen.
 * @param {FormatEntry[]} formats
 * @param {string} dpProductId
 */
function guessFamily(formats, dpProductId) {
  const counts = { wandkalender: 0, tischkalender: 0 };
  for (const format of formats) {
    if (format.products?.[dpProductId]?.orderableFromOne === true && format.family in counts) {
      counts[format.family] += 1;
    }
  }
  return counts.tischkalender > counts.wandkalender ? 'tischkalender' : 'wandkalender';
}

/**
 * Filtert und sortiert die Formatliste für ein DP-Produkt.
 * @param {{ formats?: Record<string, FormatEntry> }} map
 * @param {string} dpProductId
 * @returns {{ family: string, entries: PickerEntry[] }}
 */
export function selectFormats(map, dpProductId) {
  const formats = Object.values(map?.formats ?? {});
  const family = FAMILY_BY_PRODUCT[dpProductId] ?? guessFamily(formats, dpProductId);
  const entries = formats
    .filter((format) => format.key.startsWith(`${family}_`))
    .map((format) => ({ format, offer: format.products?.[dpProductId] }))
    .filter(({ offer }) => offer?.orderableFromOne === true && typeof offer.priceFrom === 'number')
    .sort(compareEntries);
  return { family, entries };
}

/**
 * Vorauswahl: gewünschter Schlüssel (?fmt= der Seiten-URL vor data-default-key) → Produktstandard →
 * Familienstandard → günstigstes Format.
 * @param {PickerEntry[]} entries
 * @param {string} family
 * @param {string} [preferredKey]
 */
export function pickDefaultKey(entries, family, preferredKey, dpProductId = null) {
  const has = (key) => entries.some((entry) => entry.format.key === key);
  if (preferredKey && has(preferredKey)) return preferredKey;
  const productDefault = dpProductId != null ? DEFAULT_KEY_BY_PRODUCT[dpProductId] : null;
  if (productDefault && has(productDefault)) return productDefault;
  const familyDefault = DEFAULT_KEY_BY_FAMILY[family];
  if (familyDefault && has(familyDefault)) return familyDefault;
  const cheapest = [...entries].sort((a, b) => a.offer.priceFrom - b.offer.priceFrom)[0];
  return cheapest?.format.key ?? null;
}

/**
 * Längste Seite (mm) über die GESAMTE Formatliste — einmal pro Rendern
 * berechnet, nicht über die sichtbare Teilmenge, damit die Kacheln beim
 * Aufklappen der Disclosure nicht nachträglich schrumpfen.
 * @param {PickerEntry[]} entries
 */
export function maxLongSideMm(entries) {
  let max = 0;
  for (const { format } of entries) max = Math.max(max, format.widthMm, format.heightMm);
  return max;
}

/**
 * Silhouette: Seitenverhältnis des Formats, relativ zum größten Format der
 * Liste gestaffelt. Die Box (64px) bekommt nur das größte Format; kleinere
 * schrumpfen mit (Verhältnis der langen Seiten)^0.6, sodass A5/A4/A3/A2 sich
 * sichtbar unterscheiden (≈31/38/47/58px), ohne dass A5 unlesbar wird.
 * @param {FormatEntry} format
 * @param {number} [maxLongMm] - längste Seite der Liste; Standard: das Format selbst (volle Box)
 */
export function previewSize(format, maxLongMm) {
  const longSide = Math.max(format.widthMm, format.heightMm);
  const reference = maxLongMm > 0 ? maxLongMm : longSide;
  const box = PREVIEW_BOX_PX * Math.pow(longSide / reference, PREVIEW_SCALE_EXPONENT);
  const scale = box / longSide;
  return {
    width: Math.max(PREVIEW_MIN_PX, Math.round(format.widthMm * scale)),
    height: Math.max(PREVIEW_MIN_PX, Math.round(format.heightMm * scale)),
  };
}

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} [text]
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} list - Die <ul> für die Kacheln.
 * @property {HTMLElement} status - Lade-/Fehlerzeile (role="status").
 * @property {HTMLButtonElement} [more] - Schalter „Alle Formate anzeigen“.
 *
 * @extends Component<Refs>
 */
class DpFormatPicker extends Component {
  requiredRefs = ['list', 'status'];

  /** @type {PickerEntry[]} */
  #entries = [];

  /** @type {string} */
  #family = 'wandkalender';

  /** Längste Seite (mm) der gesamten Liste — Bezugsgröße der Silhouetten. */
  #maxLongMm = 0;

  /** @type {object | null} */
  #selected = null;

  #started = false;

  /** Aktuelle Auswahl (dasselbe Objekt wie event.detail), null vor dem ersten Rendern. */
  get selection() {
    return this.#selected;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#started) return;
    this.#started = true;
    this.refs.status.textContent = TEXT.loading;
    // Papier-/Bindungs-Radios der Kaufspalte liegen außerhalb der Komponente: bei jeder Änderung die
    // aktuelle Kachel mit den Preisen der neuen Kombination erneut melden (QA 2026-09-21 F-01).
    document.addEventListener('change', this.#onPageOptionChange);
    this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onPageOptionChange);
    super.disconnectedCallback?.();
  }

  #onPageOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.dataset.dpOption) return;
    if (this.#selected?.key) this.#select(this.#selected.key);
  };

  async #load() {
    const { mapUrl, dpProductId } = this.dataset;
    if (!mapUrl || !dpProductId) {
      this.#fail();
      return;
    }

    try {
      const response = await fetch(mapUrl, { credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const map = await response.json();
      const { family, entries } = selectFormats(map, dpProductId);
      this.#family = family;
      this.#entries = entries;
    } catch (error) {
      console.warn('[dp-format-picker] Formatliste konnte nicht geladen werden', error);
      this.#fail();
      return;
    }

    if (!this.#entries.length) {
      this.#fail();
      return;
    }

    this.#render();
  }

  #fail() {
    const { list, status } = this.refs;
    this.#clearSkeleton();
    list.hidden = true;
    status.classList.remove('dp-fp__status--quiet');
    status.textContent = TEXT.failed;
  }

  /** Serverseitige Skelett-Kacheln (snippets/dp-format-picker.liquid) entfernen. */
  #clearSkeleton() {
    for (const node of this.querySelectorAll('.dp-fp__item--skeleton, [data-fp-skeleton]')) node.remove();
  }

  /** ?fmt= der Seiten-URL (geprüft, dp-designer-link.js); null ohne oder mit ungültigem Parameter. */
  #requestedKey() {
    try {
      return readDesignerParams(location.search).fmt;
    } catch {
      return null;
    }
  }

  #render() {
    const { list, status, more } = this.refs;
    // Ein per URL angefordertes Format (Startseiten-Kachel, Megamenü) gewinnt vor der Vorauswahl der
    // Section; kennt das Produkt den Schlüssel nicht, greift die normale Kette in pickDefaultKey().
    const defaultKey = pickDefaultKey(this.#entries, this.#family, this.#requestedKey() ?? this.dataset.defaultKey, this.dataset.dpProductId ?? null);
    const defaultIndex = this.#entries.findIndex((entry) => entry.format.key === defaultKey);
    this.#maxLongMm = maxLongSideMm(this.#entries);

    const requested = Number.parseInt(this.dataset.visibleCount ?? '', 10);
    let limit = Number.isFinite(requested) ? requested : DEFAULT_VISIBLE_COUNT;
    if (limit <= 0 || defaultIndex >= limit || this.#entries.length - limit < 2) {
      limit = this.#entries.length;
    }

    // Ersetzt die Skelett-Kacheln aus dem Liquid-Snippet in einem Schritt (kein Layout-Sprung).
    list.replaceChildren(
      ...this.#entries.map((entry, index) =>
        this.#buildTile(entry, {
          checked: entry.format.key === defaultKey,
          collapsed: index >= limit,
          popular: entry.format.key === defaultKey,
        })
      )
    );
    this.#clearSkeleton();
    list.hidden = false;
    status.textContent = '';
    status.hidden = true;

    if (more) {
      const hasMore = limit < this.#entries.length;
      more.hidden = !hasMore;
      more.setAttribute('aria-expanded', 'false');
      more.textContent = TEXT.showAll(this.#entries.length);
    }

    if (defaultKey) this.#select(defaultKey);
  }

  /**
   * @param {PickerEntry} entry
   * @param {{ checked: boolean, collapsed: boolean, popular: boolean }} state
   */
  #buildTile({ format, offer }, { checked, collapsed, popular }) {
    const groupName = this.dataset.name || 'dp-format';
    const id = `${groupName}-${format.key}`;

    const item = el('li', 'dp-fp__item');
    if (collapsed) {
      item.hidden = true;
      item.dataset.collapsed = 'true';
    }

    const input = el('input', 'dp-fp__input');
    input.type = 'radio';
    input.name = groupName;
    input.id = id;
    input.value = format.key;
    input.checked = checked;

    const tile = el('label', 'dp-fp__tile');
    tile.htmlFor = id;

    const preview = el('span', 'dp-fp__preview');
    preview.setAttribute('aria-hidden', 'true');
    const shape = el('span', 'dp-fp__shape');
    const { width, height } = previewSize(format, this.#maxLongMm);
    shape.style.width = `${width}px`;
    shape.style.height = `${height}px`;
    preview.append(shape);

    const check = el('span', 'dp-fp__check');
    check.setAttribute('aria-hidden', 'true');
    check.innerHTML = CHECK_ICON;

    // Leerzeichen-Textknoten zwischen den Spans: im Flex-Container unsichtbar,
    // trennen aber die Wörter im zugänglichen Namen des Radios.
    tile.append(
      preview,
      el('span', 'dp-fp__name', friendlyName(format)),
      ' ',
      el('span', 'dp-fp__size', formatSize(format)),
      ' ',
      el('span', 'dp-fp__price', `${TEXT.from} ${formatEuro(offer.priceFrom)}`),
      check
    );
    const designerDpi = Number(format.designerDpi);
    if (Number.isFinite(designerDpi) && designerDpi > 0 && designerDpi < 300) {
      tile.dataset.designerDpi = String(designerDpi);
      tile.append(' ', el('span', 'dp-fp__note', TEXT.dpi(designerDpi)));
    }
    if (popular) tile.append(' ', el('span', 'dp-fp__pill', TEXT.popular));

    item.append(input, tile);
    return item;
  }

  /** Reagiert auf on:change am <fieldset> (Radios blubbern dorthin). */
  handleChange() {
    const checked = this.querySelector('.dp-fp__input:checked');
    if (checked instanceof HTMLInputElement) this.#select(checked.value);
  }

  /** Klappt die über data-visible-count hinausgehenden Kacheln auf/zu. */
  toggleMore() {
    const { more } = this.refs;
    if (!more) return;

    const expanded = more.getAttribute('aria-expanded') === 'true';
    for (const item of this.querySelectorAll('.dp-fp__item[data-collapsed]')) {
      // Beim Zuklappen bleibt eine gewählte Kachel sichtbar, sonst verschwände die Auswahl.
      const keepVisible = expanded && item.querySelector('.dp-fp__input:checked');
      item.hidden = expanded && !keepVisible;
    }
    more.setAttribute('aria-expanded', String(!expanded));
    more.textContent = expanded ? TEXT.showAll(this.#entries.length) : TEXT.showLess;
  }

  /** @param {string} key */
  #select(key) {
    const entry = this.#entries.find((candidate) => candidate.format.key === key);
    if (!entry) return;

    const { format, offer } = entry;
    // Preis/Staffel der gewählten Papier/Bindungs-Kombination (Map `options`); ohne Datensatz Standardpreis
    // mit optionsResolved=false (Kaufspalte zeigt dann den Hinweis „Aufpreis im Bestellschritt").
    const selectedOptions = selectedPageOptions(document);
    const resolved = resolveOptionOffer(offer, selectedOptions);
    const detail = {
      key: format.key,
      dpProductId: this.dataset.dpProductId,
      variantId: offer.variantId,
      flyerVariantId: offer.flyerVariantId ?? null,
      attrs: offer.attrs ?? {},
      priceFrom: resolved?.priceFrom ?? offer.priceFrom,
      // Mengenstaffel (1/2/3/5/10) und Aufpreis des zweiten Exemplars aus der Map (build-format-prices.mjs)
      // für die Staffel-Zeile und -Tabelle der Kaufspalte (sections/dp-product-main.liquid) – für die Kombination.
      tiers: resolved ? resolved.tiers : Array.isArray(offer.tiers) ? offer.tiers.filter((t) => Number.isInteger(t?.quantity) && typeof t.price === 'number') : [],
      secondCopyExtra: resolved ? resolved.secondCopyExtra : typeof offer.secondCopyExtra === 'number' ? offer.secondCopyExtra : null,
      selectedOptions,
      optionKey: resolved?.optionKey ?? null,
      optionsResolved: resolved ? resolved.resolved : true,
      optionIsStandard: resolved ? resolved.isStandard : true,
      standardPriceFrom: resolved?.standardPriceFrom ?? offer.priceFrom,
      optionExtra: resolved?.extra ?? null,
      label: format.label,
      name: friendlyName(format),
      family: format.family,
      widthMm: format.widthMm,
      heightMm: format.heightMm,
      designerDpi: Number.isFinite(Number(format.designerDpi)) ? Number(format.designerDpi) : null,
      // Express (Inhaber 2026-09-24): Aufschlag PRO SENDUNG und Lieferzeit des Lieferanten, damit
      // Preisanzeige, Staffel und Designer-Link dieselbe Zahl nutzen. Fehlt der Block, gibt es für
      // dieses Format kein Express — die Wahl wird dann gar nicht angeboten.
      express: expressOfferFrom(offer),
      // Standard-Lieferzeit des Lieferanten („5-5"), damit die Produktseite daraus dasselbe
      // voraussichtliche Datum rechnet wie der Designer (assets/dp-lieferdatum.js).
      deliveryDays: typeof offer.deliveryDays === 'string' ? offer.deliveryDays : null,
    };
    this.#selected = detail;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(detail));
    } catch {
      // Privater Modus / gesperrter Speicher: die Auswahl funktioniert trotzdem über das Event.
    }

    this.dispatchEvent(new CustomEvent(FORMAT_CHANGE_EVENT, { bubbles: true, composed: true, detail }));
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('dp-format-picker')) {
  customElements.define('dp-format-picker', DpFormatPicker);
}
